import React, { useContext, useState } from "react";
import { remove, values } from "lodash";
import { Updater } from "use-immer";
import { PersonLookup } from "../../data/lookups";
import { FoodDto, PurchaseDto } from "../../api";
import {
  AlternativeModification,
  StepIngredientModification,
} from "../../data/dishModification";
import { FoodUnit } from "../../data/foodUnit";
import {
  formatFoodDescription,
  getDefaultFoodUnit,
  getDefaultPurchase,
  getFoodMaybe,
} from "../../utils/foodUtils";
import { getRenderKey } from "../../utils/renderUtils";
import { createPathUpdater } from "../../utils/immerUtils";
import { computeStepIngredientNutri } from "../../utils/dishUtils";
import { DrawerHistoryContext } from "../../contexts";
import { usePlan2PlateDisclosure } from "../Plan2PlateDrawer";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { NumberInput } from "../ui/Input";
import { MacroBar } from "../ui/MacroBar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import { UnitAmount } from "../UnitAmount";
import { Glyph, SmallIconButton } from "../SmallIconButton";
import { EditFoodDrawer } from "../EditFoodDrawer";
import { SearchForFoodDrawer } from "../SearchForFoodDrawer";
import { PersonChoiceDrawer } from "./PersonChoiceDrawer";
import { QueryBoundary } from "../ErrorBoundary";
import { LookupSkeleton } from "../ui/LookupSkeleton";
import { FoodName } from "./FoodName";

export const IngredientEditor = (props: {
  ingredient: StepIngredientModification;
  updateIngredient: Updater<StepIngredientModification>;
  foods: FoodDto[];
  purchases: PurchaseDto[];
  loadFood: () => Promise<FoodDto[]>;
  personLookup: PersonLookup;
  editAvailabilities: (purchaseUnitId: number) => void;
  deleteIngredient: () => void;
  fromCookbook: boolean;
  purchaseRelevant: boolean;
  hasNonDefaultPurchase: boolean;
}) => {
  const food = getFoodMaybe(props.ingredient.foodUnitId, props.foods);
  const foodUnit = food?.foodUnits.find(
    (fu) => fu.id === props.ingredient.foodUnitId,
  );
  const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState<
    number | undefined
  >(undefined);
  const selectedAlternative =
    selectedAlternativeIndex != undefined
      ? props.ingredient.alternatives[selectedAlternativeIndex]
      : undefined;
  const selectedAlternativeFood = getFoodMaybe(
    selectedAlternative?.foodUnitId,
    props.foods,
  );
  const selectedAlternativeFoodUnit: FoodUnit | undefined =
    selectedAlternativeFood?.foodUnits.find(
      (fu) => fu.id === selectedAlternative?.foodUnitId,
    );
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const updateAlternative = createPathUpdater<AlternativeModification>(
    props.updateIngredient,
    `alternatives[${selectedAlternativeIndex}]`,
  );
  const {
    isOpen: isOpenFoodDrawer,
    onOpen: onOpenFoodDrawer,
    onClose: onCloseFoodDrawer,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const getDefaultPurchaseIdForFoodId = (foodId: number) =>
    getDefaultPurchase(
      props.foods.find((f) => f.id === foodId)!,
      props.purchases,
    )!.id;
  const {
    isOpen: isOpenPersonChoiceDrawer,
    onOpen: onOpenPersonChoiceDrawer,
    onClose: onClosePersonChoiceDrawer,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const {
    isOpen: isOpenPersonChoiceAlternativeDrawer,
    onOpen: onOpenPersonChoiceAlternativeDrawer,
    onClose: onClosePersonChoiceAlternativeDrawer,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const {
    isOpen: isOpenEditFood,
    onOpen: onOpenEditFood,
    onClose: onCloseEditFood,
  } = usePlan2PlateDisclosure(drawerHistoryContext);

  // ─── Pencil-Zustand ─────────────────────────────────────────────────────────
  const [editedFood, setEditedFood] = useState<FoodDto | undefined>();

  const getActiveAlternative = () =>
    selectedAlternative != undefined &&
    selectedAlternative.foodUnitId != undefined &&
    selectedAlternative.amount != undefined
      ? selectedAlternative
      : props.ingredient;

  const submitSelectedPersons = (personIds: number[], forUnit: number) => {
    props.updateIngredient((i) => {
      const personChoices = i.personChoices;
      const { foodUnitId, amount } = getActiveAlternative();
      remove(
        personChoices,
        ({ personId, foodUnitId: foodUnitIdChoice }) =>
          (foodUnitIdChoice === forUnit && !personIds.includes(personId)) ||
          (personIds.includes(personId) && foodUnitIdChoice !== foodUnitId),
      );
      for (const personId of personIds) {
        if (!personChoices.find((p) => p.personId === personId)) {
          personChoices.push({
            foodUnitId: foodUnitId!,
            personId,
            amount: amount!,
          });
        }
      }
    });
  };

  // Das Dropdown "Alternative hinzufügen" braucht den UseMemo Block oben nicht mehr, da wir availableFoods entfernt haben
  const nutri = computeStepIngredientNutri(props.ingredient, props.foods);

  return (
    <>
      <PersonChoiceDrawer
        personChoices={props.ingredient.personChoices.filter(
          (c) => c.foodUnitId === getActiveAlternative().foodUnitId,
        )}
        onClose={onClosePersonChoiceDrawer}
        open={isOpenPersonChoiceDrawer}
        personLookup={props.personLookup}
        foods={props.foods}
        ingredientUnit={props.ingredient.foodUnitId}
        alternativeUnit={selectedAlternative?.foodUnitId}
        submitSelectedPersons={submitSelectedPersons}
      />
      <PersonChoiceDrawer
        personChoices={props.ingredient.personChoices.filter(
          (c) => c.foodUnitId === getActiveAlternative().foodUnitId,
        )}
        onClose={onClosePersonChoiceAlternativeDrawer}
        open={isOpenPersonChoiceAlternativeDrawer}
        personLookup={props.personLookup}
        foods={props.foods}
        ingredientUnit={props.ingredient.foodUnitId}
        alternativeUnit={undefined}
        submitSelectedPersons={submitSelectedPersons}
      />
      <SearchForFoodDrawer
        onClose={onCloseFoodDrawer}
        open={isOpenFoodDrawer}
        selectFood={(food) => {
          props.updateIngredient((i) =>
            i.alternatives.push({
              foodUnitId: getDefaultFoodUnit(food)?.id,
            }),
          );
          setSelectedAlternativeIndex(props.ingredient.alternatives.length);
        }}
      />
      {/* EditFoodDrawer für Stift-Aktion */}
      {editedFood && (
        <EditFoodDrawer
          allowUnitDeletion={false}
          onClose={onCloseEditFood}
          open={isOpenEditFood}
          onSubmit={async (f) => {
            await props.loadFood();
          }}
          food={editedFood}
        />
      )}{" "}
      <Card className="border-line p-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-ink">
              <QueryBoundary
                fallback={
                  <LookupSkeleton
                    dataCy="ingredient-name-loading"
                    className="h-3 w-24"
                  />
                }
              >
                <FoodName unitId={props.ingredient.foodUnitId} />
              </QueryBoundary>
            </span>
            {props.hasNonDefaultPurchase && (
              <span className="inline-flex items-center rounded-full">
                <Glyph icon="purchase" size={20} />
              </span>
            )}
          </div>
        </div>

        {props.ingredient.alternatives.length >
          (selectedAlternativeIndex != undefined ? 1 : 0) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {props.ingredient.alternatives.map((a, i) =>
              i === selectedAlternativeIndex || a == undefined ? null : (
                <Badge
                  key={getRenderKey(a, i)}
                  variant="default"
                  className="cursor-pointer"
                  onClick={() => setSelectedAlternativeIndex(i)}
                >
                  {formatFoodDescription(
                    getFoodMaybe(a?.foodUnitId, props.foods),
                  )}
                </Badge>
              ),
            )}
          </div>
        )}

        {selectedAlternativeIndex != undefined && selectedAlternativeFood && (
          <div className="mt-2 rounded-card border border-line bg-surface p-2">
            <p className="mb-2 text-sm font-semibold text-ink">
              Alternative{" "}
              <span className="text-gold">
                {formatFoodDescription(selectedAlternativeFood)}
              </span>
            </p>
            <UnitAmount
              foodUnit={selectedAlternativeFoodUnit}
              units={selectedAlternativeFood.foodUnits}
              amount={selectedAlternative?.amount}
              onUnitChange={(unit) =>
                updateAlternative((a) => {
                  a.foodUnitId = unit?.id;
                })
              }
              onAmountChange={(v) => updateAlternative((a) => (a.amount = v))}
            />
            <div className="mt-2 flex items-center justify-center gap-2">
              {!props.fromCookbook && (
                <SmallIconButton
                  label="Für Person abweichen"
                  icon="account"
                  onClick={onOpenPersonChoiceDrawer}
                  disabled={
                    !selectedAlternativeFoodUnit ||
                    !selectedAlternative ||
                    !selectedAlternative.amount
                  }
                />
              )}
              <SmallIconButton
                label="Alternative speichern"
                icon="check"
                onClick={() => setSelectedAlternativeIndex(undefined)}
                className={"[&_polyline]:!stroke-[0.5px]"}
              />
              <SmallIconButton
                label="Einkaufsoptionen"
                icon="shopCart"
                onClick={() => {
                  if (selectedAlternativeFood.id) {
                    props.editAvailabilities(
                      getDefaultPurchaseIdForFoodId(selectedAlternativeFood.id),
                    );
                  }
                }}
              />
              <SmallIconButton
                data-cy="dish-ingredient-save-alternative-button"
                label="Lebensmittel bearbeiten"
                icon="pencil"
                onClick={() => {
                  setEditedFood(selectedAlternativeFood);
                  onOpenEditFood();
                }}
              />
              <SmallIconButton
                label="Alternative löschen"
                icon="delete"
                variant="destructive"
                onClick={() => {
                  props.updateIngredient((i) => {
                    i.alternatives.splice(selectedAlternativeIndex, 1);
                    remove(
                      i.personChoices,
                      (c) => c.foodUnitId === selectedAlternativeFoodUnit?.id,
                    );
                  });
                  setSelectedAlternativeIndex(undefined);
                }}
              />
            </div>
          </div>
        )}

        <MacroBar
          className="mt-1"
          fat={nutri.fat}
          carbs={nutri.carbs}
          protein={nutri.protein}
          kcal={nutri.kcal}
          showExtendedInfo={false}
        />

        <div className="mt-2">
          <UnitAmount
            dataCy="dish-ingredient-amount-input"
            foodUnit={foodUnit}
            units={food?.foodUnits || []}
            amount={props.ingredient.amount}
            onUnitChange={(fu) =>
              props.updateIngredient((i) => {
                i.foodUnitId = fu?.id;
              })
            }
            onAmountChange={(v) =>
              props.updateIngredient((i) => {
                i.amount = v;
              })
            }
          />
        </div>

        {!props.fromCookbook && props.purchaseRelevant && (
          <div className="mt-2 flex flex-col items-stretch gap-1.5">
            {values(props.personLookup)
              .filter((p) => p.active)
              .map((person) => {
                const cIndex = props.ingredient.personChoices.findIndex(
                  (pc) => pc.personId === person.id,
                );
                const c =
                  cIndex >= 0
                    ? props.ingredient.personChoices[cIndex]
                    : {
                        personId: person.id,
                        foodUnitId: props.ingredient.foodUnitId,
                        amount: props.ingredient.amount || 0,
                      };
                const choiceFood = getFoodMaybe(c.foodUnitId, props.foods);
                const choiceUnit = choiceFood?.foodUnits.find(
                  (fu) => fu.id === c.foodUnitId,
                );
                const factor = c.amount * ((choiceUnit?.netWeight || 0) / 100);
                const alternativeTag =
                  props.ingredient.alternatives.length &&
                  choiceUnit?.id != props.ingredient.foodUnitId
                    ? `${formatFoodDescription(choiceFood)}`
                    : undefined;
                const maybeIndex = props.ingredient.alternatives.findIndex(
                  (a) => a.foodUnitId === c.foodUnitId,
                );
                return (
                  <div
                    key={person.id}
                    className="flex items-start gap-2 rounded-card border border-line bg-surface-card p-2"
                  >
                    <div className="flex flex-1 flex-col gap-0.5">
                      <div className={"flex gap-2 mb-1"}>
                        <span className="text-sm font-medium text-ink-muted">
                          {person.name}
                        </span>
                        {alternativeTag && (
                          <Badge
                            variant="default"
                            className="cursor-pointer self-start"
                            onClick={() =>
                              maybeIndex >= 0 &&
                              setSelectedAlternativeIndex(maybeIndex)
                            }
                          >
                            {alternativeTag}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <NumberInput
                          value={c.amount}
                          onValueChange={(v) =>
                            props.updateIngredient((i) => {
                              const idx = i.personChoices.findIndex(
                                (pc) => pc.personId === person.id,
                              );
                              if (idx >= 0) {
                                i.personChoices[idx].amount = v ?? 0;
                              } else {
                                i.personChoices.push({
                                  personId: person.id,
                                  foodUnitId: i.foodUnitId!,
                                  amount: v ?? 0,
                                });
                              }
                            })
                          }
                          className="h-8 w-20"
                        />
                        <Select
                          value={choiceUnit ? String(choiceUnit.id) : undefined}
                          onValueChange={(v) =>
                            props.updateIngredient((i) => {
                              const idx = i.personChoices.findIndex(
                                (pc) => pc.personId === person.id,
                              );
                              if (idx >= 0) {
                                i.personChoices[idx].foodUnitId = Number(
                                  v ?? 0,
                                );
                              } else {
                                i.personChoices.push({
                                  personId: person.id,
                                  foodUnitId: Number(v ?? 0),
                                  amount: i.amount || 0,
                                });
                              }
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-auto min-w-[5rem]">
                            <SelectValue placeholder="Einheit" />
                          </SelectTrigger>
                          <SelectContent>
                            {choiceFood?.foodUnits.map((u) => (
                              <SelectItem key={u.id} value={String(u.id)}>
                                {u.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {choiceFood && (
                        <MacroBar
                          fat={choiceFood.fat * factor}
                          carbs={choiceFood.carbs * factor}
                          protein={choiceFood.protein * factor}
                          kcal={Math.max(
                            choiceFood.kcal! * factor,
                            (choiceFood.fat * 9 +
                              choiceFood.carbs * 4 +
                              choiceFood.protein * 4) *
                              factor,
                          )}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        <div className="mt-2 flex items-center justify-center gap-2">
          {props.purchaseRelevant && (
            <>
              <SmallIconButton
                data-cy="dish-ingredient-add-alternative-button"
                label="Alternative hinzufügen"
                icon="star"
                onClick={onOpenFoodDrawer}
              />
              <SmallIconButton
                label="Einkaufsoptionen"
                icon="shopCart"
                onClick={() => {
                  if (food?.id) {
                    props.editAvailabilities(
                      getDefaultPurchaseIdForFoodId(food.id),
                    );
                  }
                }}
              />
              <SmallIconButton
                label="Lebensmittel bearbeiten"
                icon="pencil"
                onClick={() => {
                  setEditedFood(food);
                  onOpenEditFood();
                }}
              />
            </>
          )}
          <SmallIconButton
            label="Zutat löschen"
            icon="delete"
            variant="destructive"
            onClick={props.deleteIngredient}
          />
        </div>
      </Card>
    </>
  );
};
